import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Provider } from "react-redux";
import SmartTitanProjektPDF from "../../components/PDFDokumente/SmartTitanProjektPDF";
import Headline from "../../components/UI/Headline";
import MainArea from "../../components/UI/MainArea";
import { useGetTraggliedDatenQuery } from "../../services/API/titanApi";
import { selectAllResults } from "../../services/TotalCalculation";
import { selectBelastungsartUndRichtung } from "../../store/slices/belastungsartUndRichtungSlice";
import { selectBemessungenMikropfaehle } from "../../store/slices/bemessungenMikropfaehleSlice";
import { selectFrontendZustand } from "../../store/slices/frontendZustandSlice";
import { selectTragglied } from "../../store/slices/traggliedSlice";
import { useAddPdfExportMutation } from "../../services/API/statisticsAPI";
import { selectCurrentUser } from "../../store/slices/auth/authSlice";
import { store } from "../../store/store";

function calculateSettings_0() {
  let t = [];
  // #region Innere
  t.push({
    name: "euroInnere",
    value: true,
    text: "navigation.entry1.innerLoadCarryingCapacity",
  });

  // #endregion

  // #region Aussere
  t.push({
    name: "euroAussere",
    value: true,
    text: "navigation.entry2.outerLoadCarryingCapacity",
  });

  // #endregion

  // #region Pfahlkopf
  t.push({
    name: "euroAusserePfahlkopf",
    value: true,
    text: "navigation.pileHeadConstruction",
  });
  // #endregion

  // #region EDP
  // t.push({
  //   name: "edpCalculator",
  //   value: true,
  //   text: "epd.title",
  // });
  // #endregion

  return t;
}

function calculateSettings_2() {
  let t = [];
  t.push({
    name: "aashtoInnere",
    value: true,
    text: "navigation.entry1.innerLoadCarryingCapacity",
  });
  t.push({
    name: "aashtoAussere",
    value: true,
    text: "navigation.entry2.outerLoadCarryingCapacity",
  });
  // t.push({
  //   name: "edpCalculator",
  //   value: true,
  //   text: "epd.title",
  // });
  return t;
}

function calculateSettings(bemessungenmicropiles) {
  let result = [];
  result = result.concat(bemessungenmicropiles < 2 ? calculateSettings_0() : calculateSettings_2());
  return result;
}

export default function PdfExportPage() {
  const { t, i18n } = useTranslation(["translation", "fhwa"]);
  const tragglied = useSelector(selectTragglied);
  const bemessungenmicropiles = useSelector(selectBemessungenMikropfaehle);
  const frontendzustand = useSelector(selectFrontendZustand);
  const { data: traggliedDaten } = useGetTraggliedDatenQuery(tragglied.title);
  const belastungsartundrichtung = useSelector(selectBelastungsartUndRichtung);
  const results = useSelector((state) => selectAllResults(state, { traggliedDaten }));
  const [allSettingsState, setAllSettingsState] = useState(null);
  const curUser = useSelector(selectCurrentUser);
  const [pdfExport, { isLoading }] = useAddPdfExportMutation();

  if (allSettingsState === null) {
    let allSettings = calculateSettings(
      bemessungenmicropiles,
      frontendzustand,
      belastungsartundrichtung
    );
    setAllSettingsState(allSettings);
  }

  const printDocument = async (event, state) => {
    event.preventDefault();
    const title =
      bemessungenmicropiles < 2
        ? tragglied?.title
        : bemessungenmicropiles === 2
          ? results?.Aashto?.aashtoAll?.selectedCarryingElement?.name
          : results?.Fhwa?.fhwaAll?.selectedCarryingElement?.name;

    const length =
      bemessungenmicropiles < 2
        ? results?.Germany?.Aussere?.BestimmungGesamtlaenge?.pfahllaenge === 0
          ? -1
          : results?.Germany?.Aussere?.BestimmungGesamtlaenge?.pfahllaenge
        : bemessungenmicropiles === 2
          ? results?.Aashto?.aashtoAll?.inclinedLengthTableSum?.metric === null
            ? -1
            : (results?.Aashto?.aashtoAll?.inclinedLengthTableSum?.metric ?? -1)
          : results?.Fhwa?.fhwaAll?.inclinedLengthTableSum?.metric === null
            ? -1
            : (results?.Fhwa?.fhwaAll?.inclinedLengthTableSum?.metric ?? -1);

    const { data, error } = await pdfExport({
      data: {
        username: curUser,
        results: {
          carryingElement: title,
          berechnungsModus: bemessungenmicropiles,
          exportAt: new Date(),
          language: i18n.language,
          length: length ?? -1,
        },
      },
    });

    // Ensure results has the correct Deckblatt structure
    const pdfData = {
      ...results,
      Deckblatt: {
        ...results?.Deckblatt,
        PreparedBy: {
          company: results?.Deckblatt?.PreparedBy?.company || "",
          username: results?.Deckblatt?.PreparedBy?.username || curUser || "",
          createdAt: new Date().toLocaleDateString(),
        },
      },
    };

    const blob = await pdf(
      <Provider store={store}>
        <SmartTitanProjektPDF data={pdfData} settings={allSettingsState} />
      </Provider>
    ).toBlob();
    saveAs(blob, "Zusammenfassung.pdf");
  };

  function handleChecked(e) {
    let allsettings = allSettingsState.map((item) => {
      if (item.name === e.target.value) {
        return { ...item, value: !item.value };
      }
      return item;
    });
    setAllSettingsState(allsettings);
  }

  useEffect(() => {}, [allSettingsState]);

  return (
    <MainArea>
      <Headline isPrimary text={t("createpdf.title")} />
      <div>
        <Row>
          <Col md={8}>
            <Row>
              <Col>
                <fieldset>
                  <h3>{t("createpdf.settings")}</h3>
                  {allSettingsState?.map((item, index) => (
                    <div key={index}>
                      <input
                        id={item.name}
                        name={item.name}
                        type="checkbox"
                        value={item.name}
                        checked={item.value}
                        disabled={item.disabled}
                        onChange={(e) => {
                          handleChecked(e);
                        }}
                      />
                      <span className="setting-label">
                        {item.text.startsWith("epd") ? (
                          <>
                            {"CO"}
                            <sub>2</sub>
                            {t(item.text)}
                          </>
                        ) : (
                          t(item.text)
                        )}
                      </span>
                    </div>
                  ))}
                </fieldset>
                <button className="link-button mt-3" onClick={printDocument}>
                  {t("createpdf.title")}
                </button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </MainArea>
  );
}
